import React from "react";
import { Helmet } from "react-helmet";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: object;
  canonical?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = "iAssets | Building iBird - Decentralized Social Media on Hedera",
  description = "iAssets is building iBird, a revolutionary decentralized social media platform on Hedera Hashgraph. Experience Web3 social networking with HBAR, ASSET tokens, and true digital freedom.",
  keywords = "iAssets, iBird, Hedera, HBAR, ASSET, Web3, Decentralized Social Media, Blockchain, Cryptocurrency, DeFi, SocialFi, Hedera Hashgraph, HCS, HTS, Token Burning, Cross-chain",
  image = "https://iassets.org/logo-500px.jpg",
  url = "https://iassets.org",
  type = "website",
  author = "iAssets Team",
  publishedTime,
  modifiedTime,
  structuredData,
  canonical,
}) => {
  const siteUrl = "https://iassets.org";
  const fullUrl = url.startsWith("http") ? url : `${siteUrl}${url}`;
  const canonicalUrl = canonical || fullUrl;
  const fullImageUrl = image.startsWith("http") ? image : `${siteUrl}${image}`;

  // Default structured data for the organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        name: "iAssets",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}/logo-500px.jpg`,
          width: 500,
          height: 500,
        },
        description:
          "Building iBird - A decentralized social media platform on Hedera Hashgraph",
        foundingDate: "2023",
        sameAs: [
          "https://twitter.com/iassetsorg",
          "https://x.com/iassetsorg",
          "https://github.com/iassetsorg",
          "https://discord.gg/xM7SkkTEAG",
          "https://github.com/iassetsorg/Project-Harriet",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          url: "https://discord.gg/xM7SkkTEAG",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: siteUrl,
        name: "iAssets",
        description: description,
        publisher: {
          "@id": `${siteUrl}/#organization`,
        },
        inLanguage: "en-US",
      },
      {
        "@type": "WebPage",
        "@id": `${fullUrl}/#webpage`,
        url: fullUrl,
        name: title,
        description: description,
        isPartOf: {
          "@id": `${siteUrl}/#website`,
        },
        about: {
          "@id": `${siteUrl}/#organization`,
        },
        datePublished: publishedTime || "2023-01-01T00:00:00+00:00",
        dateModified: modifiedTime || new Date().toISOString(),
        inLanguage: "en-US",
      },
      {
        "@type": "SoftwareApplication",
        name: "iBird",
        description:
          "Decentralized social media platform built on Hedera Hashgraph",
        applicationCategory: "SocialNetworkingApplication",
        operatingSystem: "Web Browser",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
        creator: {
          "@id": `${siteUrl}/#organization`,
        },
      },
    ],
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Language and Locale */}
      <meta httpEquiv="content-language" content="en-US" />
      <meta name="language" content="English" />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta
        property="og:image:alt"
        content="iAssets - Building iBird on Hedera"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="iAssets" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@iAssetsOrg" />
      <meta name="twitter:creator" content="@iAssetsOrg" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta
        name="twitter:image:alt"
        content="iAssets - Building iBird on Hedera"
      />

      {/* Additional Meta Tags for Web3/Crypto */}
      <meta name="theme-color" content="#FF6B6B" />
      <meta name="msapplication-TileColor" content="#FF6B6B" />
      <meta name="application-name" content="iAssets" />
      <meta name="apple-mobile-web-app-title" content="iAssets" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Security Headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />

      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect"
        href="https://mainnet-public.mirrornode.hedera.com"
      />
      <link rel="preconnect" href="https://api.bscscan.com" />

      {/* DNS Prefetch for better performance */}
      <link rel="dns-prefetch" href="//github.com" />
      <link rel="dns-prefetch" href="//discord.gg" />
      <link rel="dns-prefetch" href="//x.com" />
      <link rel="dns-prefetch" href="//pancakeswap.finance" />
      <link rel="dns-prefetch" href="//saucerswap.finance" />

      {/* Article specific meta tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:section" content="Technology" />
          <meta property="article:tag" content="Blockchain" />
          <meta property="article:tag" content="Hedera" />
          <meta property="article:tag" content="Web3" />
          <meta property="article:tag" content="Social Media" />
        </>
      )}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
